import ListActionsMenu from "../documents/ListActionsMenu";
import SifenReportModal from "../sifen-reports/SifenReportModal";
import {
  CButton,
  CButtonGroup,
  CDropdown,
  CDropdownItem,
  CDropdownToggle,
  CTooltip,
} from "@coreui/react";
import {
  CREDIT_NOTE_STATUS_APPROVED_BY_SET,
  CREDIT_NOTE_STATUS_CANCELLATION_FAILED,
  CREDIT_NOTE_STATUS_ERROR,
  CREDIT_NOTE_STATUS_PENDING,
  CREDIT_NOTE_STATUS_RECOVERABLE_ERROR,
  CREDIT_NOTE_STATUS_RESEND_REQUESTED,
  CREDIT_NOTE_STATUS_SET_UNKNOWN_ERROR,
  CREDIT_NOTE_STATUS_UPLOADED_TO_SET,
  CreditNote,
} from "../../models/credit-note";
import React, { useEffect, useState } from "react";
import { useSub } from "../../hooks/pub-sub";
import {
  CREDIT_NOTES_DESTROY,
  CREDIT_NOTES_RETRIEVE,
  CREDIT_NOTES_UPDATE,
} from "../../auth/permissions";
import PermissionRequiredComponent from "../permissions/PermissionRequiredComponent";

const MAX_VISIBLE_ACTIONS = 3;

interface CreditNoteCrudAtionsProps {
  creditNote: CreditNote;
  onCancellationClick: (_: CreditNote) => void;
  onCancellationRetryClick: (_: CreditNote) => void;
  onCancellationRevertClick: (_: CreditNote) => void;
  onReadClick: (_: CreditNote) => void;
  onUpdateClick: (_: CreditNote) => void;
  onDeleteClick: (_: CreditNote) => void;
  onConsultByCdcClick: (_: CreditNote) => void;
  onResendClick: (_: CreditNote) => void;
  onSendEmailClick: (_: CreditNote) => void;
  onHistoryClick: (_: CreditNote) => void;
}

const CreditNoteCrudActions: React.FC<CreditNoteCrudAtionsProps> = ({
  creditNote,
  onCancellationClick,
  onCancellationRetryClick,
  onCancellationRevertClick,
  onReadClick,
  onUpdateClick,
  onDeleteClick,
  onConsultByCdcClick,
  onResendClick,
  onSendEmailClick,
  onHistoryClick,
}) => {
  const [showSifenReport, setShowSifenReport] = useState(false);
  const [currentCreditNote, setCurrentCreditNote] = useState(creditNote);

  useEffect(() => {
    setCurrentCreditNote(creditNote);
  }, [creditNote]);

  useSub(`credit_note_${creditNote.id}`, (creditNote: CreditNote) => {
    setCurrentCreditNote(creditNote);
  });
  const cancelButton = (
    <PermissionRequiredComponent permissionName={CREDIT_NOTES_UPDATE}>
      <CTooltip content="Anular">
        <CButton
          className="text-white"
          color="secondary"
          onClick={() => {
            onCancellationClick(creditNote);
          }}
        >
          <i className="fa fa-ban"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const editButton = (
    <PermissionRequiredComponent permissionName={CREDIT_NOTES_UPDATE}>
      <CTooltip content="Editar">
        <CButton
          className="text-white"
          color="warning"
          onClick={() => {
            onUpdateClick(creditNote);
          }}
        >
          <i className="fa fa-pencil"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const deleteButton = (
    <PermissionRequiredComponent permissionName={CREDIT_NOTES_DESTROY}>
      <CTooltip content="Eliminar">
        <CButton
          className="text-white"
          color="danger"
          onClick={() => {
            onDeleteClick(creditNote);
          }}
        >
          <i className="fa fa-trash"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const documentDateTime = new Date(currentCreditNote.createdAt!).getTime();
  const now = new Date().getTime();

  const olderThan45Days = documentDateTime < now - 45 * 24 * 60 * 60 * 1000;

  const shouldShowCancelButton =
    currentCreditNote.status === CREDIT_NOTE_STATUS_APPROVED_BY_SET &&
    !olderThan45Days;
  const shouldShowEditAndDeleteButton =
    (currentCreditNote.status === CREDIT_NOTE_STATUS_PENDING ||
      currentCreditNote.status === CREDIT_NOTE_STATUS_ERROR ||
      currentCreditNote.status === CREDIT_NOTE_STATUS_SET_UNKNOWN_ERROR ||
      currentCreditNote.setResponseCode === "1001" ||
      currentCreditNote.setResponseCode === "4003") &&
    currentCreditNote.status !== CREDIT_NOTE_STATUS_RECOVERABLE_ERROR;

  const consultByCdcButton = (
    <PermissionRequiredComponent permissionName={CREDIT_NOTES_UPDATE}>
      <CTooltip content="Consultar por CDC">
        <CButton
          className="text-white"
          color="primary"
          onClick={() => {
            onConsultByCdcClick(creditNote);
          }}
        >
          <i className="fa fa-question-circle"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const shouldShowConsultByCdcButton =
    currentCreditNote.status === CREDIT_NOTE_STATUS_UPLOADED_TO_SET ||
    currentCreditNote.status === CREDIT_NOTE_STATUS_ERROR ||
    currentCreditNote.status === CREDIT_NOTE_STATUS_RESEND_REQUESTED ||
    currentCreditNote.setResponseCode === "1001" ||
    currentCreditNote.setResponseCode === "4003";

  const resendButton = (
    <PermissionRequiredComponent permissionName={CREDIT_NOTES_UPDATE}>
      <CTooltip content="Reenviar">
        <CButton
          className="text-white"
          color="success"
          onClick={() => {
            onResendClick(creditNote);
          }}
        >
          <i className="fa fa-refresh"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const shouldShowResendButton =
    currentCreditNote.status === CREDIT_NOTE_STATUS_ERROR;

  const sendEmailButton = (
    <PermissionRequiredComponent permissionName={CREDIT_NOTES_UPDATE}>
      <CTooltip
        content={creditNote.emailSent ? "Reenviar Correo" : "Enviar Correo"}
      >
        <CButton
          className="text-white"
          color="primary"
          onClick={() => {
            onSendEmailClick(creditNote);
          }}
        >
          <i className="fa fa-envelope"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const shouldShowSendEmailButton =
    currentCreditNote.status === CREDIT_NOTE_STATUS_APPROVED_BY_SET;

  const retryCancellationButton = (
    <PermissionRequiredComponent permissionName={CREDIT_NOTES_UPDATE}>
      <CTooltip content="Reintentar Anulación">
        <CButton
          className="text-white"
          color="warning"
          onClick={() => {
            onCancellationRetryClick(currentCreditNote);
          }}
        >
          <i className="fa fa-rotate-right"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const shouldShowRetryCancellationButton =
    creditNote.status === CREDIT_NOTE_STATUS_CANCELLATION_FAILED;

  const revertCancellationButton = (
    <PermissionRequiredComponent permissionName={CREDIT_NOTES_UPDATE}>
      <CTooltip content="Revertir Anulación">
        <CButton
          className="text-white"
          color="success"
          onClick={() => {
            onCancellationRevertClick(currentCreditNote);
          }}
        >
          <i className="fa fa-backward"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const shouldShowRevertCancellationButton =
    creditNote.status === CREDIT_NOTE_STATUS_CANCELLATION_FAILED;

  const detailButton = (
    <PermissionRequiredComponent permissionName={CREDIT_NOTES_RETRIEVE}>
      <CTooltip content="Ver Detalle">
        <CButton
          className="text-white"
          color="info"
          onClick={() => {
            onReadClick(currentCreditNote);
          }}
        >
          <i className="fa fa-eye"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const historyButton = (
    <PermissionRequiredComponent permissionName={CREDIT_NOTES_RETRIEVE}>
      <CTooltip content="Ver Historial">
        <CButton
          className="text-white"
          color="dark"
          onClick={() => {
            onHistoryClick(currentCreditNote);
          }}
        >
          <i className="fa fa-history"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const allActions = [
    {
      element: detailButton,
      shouldShow: true,
      label: "Ver Detalle",
      icon: "fa-eye",
      permission: CREDIT_NOTES_RETRIEVE,
      onClick: () => onReadClick(currentCreditNote),
    },
    {
      element: historyButton,
      shouldShow: true,
      label: "Ver Historial",
      icon: "fa-history",
      permission: CREDIT_NOTES_RETRIEVE,
      onClick: () => onHistoryClick(currentCreditNote),
    },
    {
      element: cancelButton,
      shouldShow: shouldShowCancelButton,
      label: "Anular",
      icon: "fa-ban",
      permission: CREDIT_NOTES_UPDATE,
      onClick: () => onCancellationClick(creditNote),
    },
    {
      element: editButton,
      shouldShow: shouldShowEditAndDeleteButton,
      label: "Editar",
      icon: "fa-pencil",
      permission: CREDIT_NOTES_UPDATE,
      onClick: () => onUpdateClick(creditNote),
    },
    {
      element: deleteButton,
      shouldShow: shouldShowEditAndDeleteButton,
      label: "Eliminar",
      icon: "fa-trash",
      permission: CREDIT_NOTES_DESTROY,
      onClick: () => onDeleteClick(creditNote),
    },
    {
      element: consultByCdcButton,
      shouldShow: shouldShowConsultByCdcButton,
      label: "Consultar por CDC",
      icon: "fa-question-circle",
      permission: CREDIT_NOTES_UPDATE,
      onClick: () => onConsultByCdcClick(creditNote),
    },
    {
      element: resendButton,
      shouldShow: shouldShowResendButton,
      label: "Reenviar",
      icon: "fa-refresh",
      permission: CREDIT_NOTES_UPDATE,
      onClick: () => onResendClick(creditNote),
    },
    {
      element: retryCancellationButton,
      shouldShow: shouldShowRetryCancellationButton,
      label: "Reintentar Anulación",
      icon: "fa-rotate-right",
      permission: CREDIT_NOTES_UPDATE,
      onClick: () => onCancellationRetryClick(currentCreditNote),
    },
    {
      element: revertCancellationButton,
      shouldShow: shouldShowRevertCancellationButton,
      label: "Revertir Anulación",
      icon: "fa-backward",
      permission: CREDIT_NOTES_UPDATE,
      onClick: () => onCancellationRevertClick(currentCreditNote),
    },
    {
      element: sendEmailButton,
      shouldShow: shouldShowSendEmailButton,
      label: creditNote.emailSent ? "Reenviar Correo" : "Enviar Correo",
      icon: "fa-envelope",
      permission: CREDIT_NOTES_UPDATE,
      onClick: () => onSendEmailClick(creditNote),
    },
    {
      element: (
        <PermissionRequiredComponent permissionName={CREDIT_NOTES_UPDATE}>
          <CTooltip content="Preparar reporte para DNIT">
            <CButton color="warning" aria-label="Preparar reporte para DNIT" onClick={() => setShowSifenReport(true)}>
              <i className="fa fa-flag"></i>
            </CButton>
          </CTooltip>
        </PermissionRequiredComponent>
      ),
      shouldShow: currentCreditNote.status === "cancelled",
      label: "Preparar reporte para DNIT",
      icon: "fa-flag",
      permission: CREDIT_NOTES_UPDATE,
      onClick: () => setShowSifenReport(true),
    },
  ];

  const visibleActions = allActions.filter((a) => a.shouldShow);
  const shouldCollapse = visibleActions.length > MAX_VISIBLE_ACTIONS;
  const primaryActions = shouldCollapse
    ? visibleActions.slice(0, MAX_VISIBLE_ACTIONS - 1)
    : visibleActions;
  const overflowActions = shouldCollapse
    ? visibleActions.slice(MAX_VISIBLE_ACTIONS - 1)
    : [];

  return (
    <td className="text-center">
      <CButtonGroup>
        {primaryActions.map((a, i) => (
          <React.Fragment key={i}>{a.element}</React.Fragment>
        ))}
        {shouldCollapse && (
          <CDropdown className="btn-group">
            <CTooltip content="Más opciones">
              <CDropdownToggle
                color="primary"
                className="text-white"
                caret={false}
                style={{ borderTopRightRadius: "0.25rem", borderBottomRightRadius: "0.25rem" }}
              >
                <i className="fa fa-ellipsis-v"></i>
              </CDropdownToggle>
            </CTooltip>
            <ListActionsMenu>
              {overflowActions.map((a, i) => (
                <PermissionRequiredComponent
                  key={i}
                  permissionName={a.permission}
                >
                  <CDropdownItem onClick={a.onClick}>
                    <i className={`fa ${a.icon} mr-2`}></i>
                    {a.label}
                  </CDropdownItem>
                </PermissionRequiredComponent>
              ))}
            </ListActionsMenu>
          </CDropdown>
        )}
      </CButtonGroup>
      {showSifenReport && <SifenReportModal
        show={showSifenReport}
        documentType="electronic_credit_note"
        documentId={currentCreditNote.id}
        onClose={() => setShowSifenReport(false)}
      />}
    </td>
  );
};

export default CreditNoteCrudActions;
